import React, { useCallback, useMemo } from 'react';
import { Pressable, StyleSheet, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore, useToastStore } from '@/store';
import Icon from '@/components/Icon';
import useRequireAuth from '@/hooks/useRequireAuth';
import { trackEvent } from '@/constants/utils/analytics';
import { mediumTap } from '@/constants/utils/haptics';

/**
 * Heart to favorite an inference task. Stops press propagation so cards /
 * rows do not open. Anonymous visitors are routed through the shared
 * `useRequireAuth` hook so they see the unified `AuthCTAModal`.
 */
function TaskFavoriteHeart({ task, hitSlop = 10 }) {
  const { colors } = useTheme();
  const { addToast } = useToastStore(s => s.actions);
  const hasUser = useAuthStore(s => Boolean(s.user?.id));
  const favoriteTaskIds = useAuthStore(s => s.user?.favoriteTaskIds);
  const toggleFavoriteTask = useAuthStore(s => s.toggleFavoriteTask);
  const requireAuth = useRequireAuth();

  const taskId = task?.id != null ? String(task.id) : '';
  const isFavorited = !!(taskId && (favoriteTaskIds || []).map(String).includes(taskId));

  const styles = useMemo(() => StyleSheet.create({
    hit: {
      padding: 6,
      borderRadius: 8,
      justifyContent: 'center',
      alignItems: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
  }), []);

  const onPress = useCallback(async (e) => {
    e?.stopPropagation?.();
    if (!taskId) return;
    const ran = requireAuth(
      async () => {
        try {
          const nowFavorited = await toggleFavoriteTask(taskId);
          mediumTap();
          trackEvent('tasks', nowFavorited ? 'tasks_favorite' : 'tasks_unfavorite', taskId);
        } catch (_) {
          addToast('Could not update favorite', 'error');
        }
      },
      {
        source: 'tasks_favorite_heart',
        title: 'Sign in to favorite tasks',
        subtitle: 'Favorites stay tied to your account so they follow you across devices.',
        icon: 'heart',
      }
    );
    if (ran === undefined) {
      trackEvent('tasks', 'tasks_favorite_auth_required', taskId);
    }
  }, [taskId, requireAuth, toggleFavoriteTask, addToast]);

  // Favoriting is account-bound; logged-out visitors don't see the control.
  if (!taskId || !hasUser) return null;

  return (
    <Pressable
      onPress={onPress}
      hitSlop={hitSlop}
      style={({ hovered, pressed }) => [
        styles.hit,
        (hovered || pressed) && { backgroundColor: colors.solid_color_tertiary },
      ]}
      accessibilityRole="button"
      accessibilityLabel={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      accessibilityState={{ selected: isFavorited }}
    >
      <Icon
        name="heart"
        size={15}
        solid={isFavorited}
        color={isFavorited ? colors.text_color_primary : colors.text_color_tertiary}
      />
    </Pressable>
  );
}

export default React.memo(TaskFavoriteHeart);
